import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DateInputValidationError } from '@cbhq/cds-common/dates/DateInputValidationError';
import { useMultiSelect } from '@cbhq/cds-common/select/useMultiSelect';
import { Select } from '@cbhq/cds-web/alpha/select';
import { Button, IconButton } from '@cbhq/cds-web/buttons';
import { DatePicker } from '@cbhq/cds-web/dates/DatePicker';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import type { TrayRefProps } from '@cbhq/cds-web/overlays/tray/Tray';
import { Tray } from '@cbhq/cds-web/overlays/tray/Tray';
import { PageFooter } from '@cbhq/cds-web/page/PageFooter';
import { Text } from '@cbhq/cds-web/typography/Text';
import { ActionType, ComponentType, logEvent } from '@cbhq/client-analytics';
import { defineMessages, useIntl } from '@cbhq/i18n';

import { logError } from '../../../utils/logging';

const messages = defineMessages({
  title: {
    id: 'Activity.auditExportTray.title',
    defaultMessage: 'Download activity',
    description: 'Title for the audit export tray',
  },
  closeAccessibilityLabel: {
    id: 'Activity.auditExportTray.closeAccessibilityLabel',
    defaultMessage: 'Close download activity tray',
    description: 'Accessibility label for the audit export tray close button',
  },
  actionTypeLabel: {
    id: 'Activity.auditExportTray.actionTypeLabel',
    defaultMessage: 'Action type',
    description: 'Label for the action type select field',
  },
  actionTypePlaceholder: {
    id: 'Activity.auditExportTray.actionTypePlaceholder',
    defaultMessage: 'All action types',
    description: 'Placeholder for the action type select field',
  },
  actorLabel: {
    id: 'Activity.auditExportTray.actorLabel',
    defaultMessage: 'Actor',
    description: 'Label for the actor select field',
  },
  actorPlaceholder: {
    id: 'Activity.auditExportTray.actorPlaceholder',
    defaultMessage: 'All actors',
    description: 'Placeholder for the actor select field',
  },
  startDateLabel: {
    id: 'Activity.auditExportTray.startDateLabel',
    defaultMessage: 'Start date',
    description: 'Label for the start date picker',
  },
  endDateLabel: {
    id: 'Activity.auditExportTray.endDateLabel',
    defaultMessage: 'End date',
    description: 'Label for the end date picker',
  },
  dateHelperText: {
    id: 'Activity.auditExportTray.dateHelperText',
    defaultMessage: 'mm/dd/yyyy',
    description: 'Helper text for the date picker fields',
  },
  invalidDateError: {
    id: 'Activity.auditExportTray.invalidDateError',
    defaultMessage: 'Please enter a valid date',
    description: 'Error message for invalid date input',
  },
  openStartDateCalendar: {
    id: 'Activity.auditExportTray.openStartDateCalendar',
    defaultMessage: 'Open start date calendar',
    description: 'Accessibility label for opening the start date calendar',
  },
  openEndDateCalendar: {
    id: 'Activity.auditExportTray.openEndDateCalendar',
    defaultMessage: 'Open end date calendar',
    description: 'Accessibility label for opening the end date calendar',
  },
  closeCalendar: {
    id: 'Activity.auditExportTray.closeCalendar',
    defaultMessage: 'Close calendar',
    description: 'Accessibility label for closing the date calendar',
  },
  nextMonth: {
    id: 'Activity.auditExportTray.nextMonth',
    defaultMessage: 'Next month',
    description: 'Accessibility label for the next month button in the calendar',
  },
  previousMonth: {
    id: 'Activity.auditExportTray.previousMonth',
    defaultMessage: 'Previous month',
    description: 'Accessibility label for the previous month button in the calendar',
  },
  actionTypeSelectAll: {
    id: 'Activity.auditExportTray.actionTypeSelectAll',
    defaultMessage: 'Select all action types',
    description: 'Label for the select all option in the action type multi-select',
  },
  actionTypeClearAll: {
    id: 'Activity.auditExportTray.actionTypeClearAll',
    defaultMessage: 'Clear all action types',
    description: 'Label for the clear all option in the action type multi-select',
  },
  actorSelectAll: {
    id: 'Activity.auditExportTray.actorSelectAll',
    defaultMessage: 'Select all actors',
    description: 'Label for the select all option in the actor multi-select',
  },
  actorClearAll: {
    id: 'Activity.auditExportTray.actorClearAll',
    defaultMessage: 'Clear all actors',
    description: 'Label for the clear all option in the actor multi-select',
  },
  cancelButton: {
    id: 'Activity.auditExportTray.cancelButton',
    defaultMessage: 'Cancel',
    description: 'Label for the cancel button in the audit export tray',
  },
  exportButton: {
    id: 'Activity.auditExportTray.exportButton',
    defaultMessage: 'Export',
    description: 'Label for the export button in the audit export tray',
  },
});

export type AuditExportSelectOption = {
  value: string;
  label: string;
};

export type AuditExportParams = {
  actionTypes: string[];
  actors: string[];
  startDate: Date | null;
  endDate: Date | null;
};

type AuditExportTrayProps = {
  onClose: () => void;
  onExport: (params: AuditExportParams) => void | Promise<void>;
  actionTypeOptions: AuditExportSelectOption[];
  actorOptions: AuditExportSelectOption[];
};

const TRAY_STYLES = {
  container: { width: '532px' },
  content: { overflow: 'visible' },
} as const;

const TRAY_TITLE_ID = 'audit-export-tray-title';

export function AuditExportTray({
  onClose,
  onExport,
  actionTypeOptions,
  actorOptions,
}: AuditExportTrayProps) {
  const { formatMessage } = useIntl();
  const trayRef = useRef<TrayRefProps>(null);

  const { value: actionTypes, onChange: onActionTypesChange } = useMultiSelect<string>({
    initialValue: [],
  });
  const { value: actors, onChange: onActorsChange } = useMultiSelect<string>({
    initialValue: [],
  });
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [startDateError, setStartDateError] = useState<DateInputValidationError | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [endDateError, setEndDateError] = useState<DateInputValidationError | null>(null);

  const title = formatMessage(messages.title);

  useEffect(() => {
    logEvent('activity_export_tray_opened', {
      componentType: ComponentType.modal,
      action: ActionType.view,
    });
  }, []);

  const closeTray = useCallback(() => {
    trayRef.current?.close();
  }, []);

  const handleCloseComplete = useCallback(() => {
    logEvent('activity_export_tray_closed', {
      componentType: ComponentType.modal,
      action: ActionType.dismiss,
    });
    onClose();
  }, [onClose]);

  const handleClose = useCallback(() => {
    closeTray();
  }, [closeTray]);

  const hasDateErrors = !!startDateError || !!endDateError;

  const handleExport = useCallback(async () => {
    logEvent('activity_export_submitted', {
      componentType: ComponentType.button,
      action: ActionType.click,
    });

    try {
      await onExport({ actionTypes, actors, startDate, endDate });
      logEvent('activity_export_success', {
        componentType: ComponentType.api,
        action: ActionType.api,
      });
      closeTray();
    } catch (error) {
      logEvent('activity_export_failed', {
        componentType: ComponentType.api,
        action: ActionType.api,
      });
      logError(error, { context: 'AuditExportTray.handleExport', isHandled: true });
    }
  }, [onExport, actionTypes, actors, startDate, endDate, closeTray]);

  const handleChangeStartDate = useCallback(
    (date: Date | null) => {
      setStartDate(date);
      if (date && endDate && date > endDate) {
        setEndDate(null);
        setEndDateError(null);
      }
    },
    [endDate],
  );

  const handleStartDateError = useCallback((error: DateInputValidationError | null) => {
    setStartDateError(error);
  }, []);

  const handleChangeEndDate = useCallback((date: Date | null) => {
    setEndDate(date);
  }, []);

  const handleEndDateError = useCallback((error: DateInputValidationError | null) => {
    setEndDateError(error);
  }, []);

  const highlightedDates = useMemo(() => {
    if (startDate && endDate && startDate < endDate) {
      return [[startDate, endDate]] as [Date, Date][];
    }
    if (startDate) {
      return [startDate];
    }
    return undefined;
  }, [startDate, endDate]);

  return (
    <Tray
      ref={trayRef}
      accessibilityLabel={title}
      accessibilityLabelledBy={TRAY_TITLE_ID}
      hideCloseButton
      onCloseComplete={handleCloseComplete}
      pin="right"
      preventDismiss
      styles={TRAY_STYLES}
      title={
        <HStack width="100%" alignItems="center" justifyContent="space-between">
          <Text id={TRAY_TITLE_ID} font="title3">
            {title}
          </Text>
          <IconButton
            transparent
            color="fg"
            name="close"
            onClick={handleClose}
            accessibilityLabel={formatMessage(messages.closeAccessibilityLabel)}
          />
        </HStack>
      }
      footer={
        <PageFooter
          height="auto"
          justifyContent="flex-end"
          action={
            <HStack gap={1}>
              <Button variant="secondary" transparent compact onClick={handleClose}>
                {formatMessage(messages.cancelButton)}
              </Button>
              <Button variant="primary" compact onClick={handleExport} disabled={hasDateErrors}>
                {formatMessage(messages.exportButton)}
              </Button>
            </HStack>
          }
        />
      }
    >
      <VStack width="100%" gap={2} paddingTop={2}>
        <Select
          type="multi"
          label={formatMessage(messages.actionTypeLabel)}
          value={actionTypes}
          onChange={onActionTypesChange}
          options={actionTypeOptions}
          placeholder={formatMessage(messages.actionTypePlaceholder)}
          selectAllLabel={formatMessage(messages.actionTypeSelectAll)}
          clearAllLabel={formatMessage(messages.actionTypeClearAll)}
          testID="audit-export-action-type-select"
        />

        <Select
          type="multi"
          label={formatMessage(messages.actorLabel)}
          value={actors}
          onChange={onActorsChange}
          options={actorOptions}
          placeholder={formatMessage(messages.actorPlaceholder)}
          selectAllLabel={formatMessage(messages.actorSelectAll)}
          clearAllLabel={formatMessage(messages.actorClearAll)}
          testID="audit-export-actor-select"
        />

        <HStack gap={1} width="100%">
          <DatePicker
            date={startDate}
            error={startDateError}
            onChangeDate={handleChangeStartDate}
            onErrorDate={handleStartDateError}
            highlightedDates={highlightedDates}
            label={formatMessage(messages.startDateLabel)}
            helperText={formatMessage(messages.dateHelperText)}
            invalidDateError={formatMessage(messages.invalidDateError)}
            openCalendarAccessibilityLabel={formatMessage(messages.openStartDateCalendar)}
            closeCalendarAccessibilityLabel={formatMessage(messages.closeCalendar)}
            nextArrowAccessibilityLabel={formatMessage(messages.nextMonth)}
            previousArrowAccessibilityLabel={formatMessage(messages.previousMonth)}
            width="49%"
          />
          <DatePicker
            date={endDate}
            disabled={!startDate}
            error={endDateError}
            minDate={startDate ?? undefined}
            onChangeDate={handleChangeEndDate}
            onErrorDate={handleEndDateError}
            highlightedDates={highlightedDates}
            label={formatMessage(messages.endDateLabel)}
            helperText={formatMessage(messages.dateHelperText)}
            invalidDateError={formatMessage(messages.invalidDateError)}
            openCalendarAccessibilityLabel={formatMessage(messages.openEndDateCalendar)}
            closeCalendarAccessibilityLabel={formatMessage(messages.closeCalendar)}
            nextArrowAccessibilityLabel={formatMessage(messages.nextMonth)}
            previousArrowAccessibilityLabel={formatMessage(messages.previousMonth)}
            width="49%"
          />
        </HStack>
      </VStack>
    </Tray>
  );
}
